// Calendar-based scope windows used by the Week / Current Month / Last Month / YTD toggle.
//   "today"          → today + yesterday (open-ended, matches backend /stats/overview)
//   "week"           → current calendar week (Monday 00:00 → now)
//   "month"          → alias for "current_month" (legacy value, kept for callers we haven't migrated)
//   "current_month"  → current calendar month (1st 00:00 → now)
//   "last_month"     → previous calendar month (1st → last day, INCLUSIVE)
//   "ytd"            → current year (Jan 1 → now)
//   "all" / undefined → no filtering
//
// scopeRange() returns { start, end } where end is null for open-ended ranges
// (everything except last_month). makeScopeFilter() builds a compatible predicate.

export function scopeRange(scope) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (scope === "today") {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return { start: yesterday, end: null };
  }
  if (scope === "week") {
    // JS Date.getDay(): 0=Sun..6=Sat. Convert to ISO weekday (0=Mon..6=Sun).
    const isoDow = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - isoDow);
    return { start: monday, end: null };
  }
  if (scope === "month" || scope === "current_month") {
    return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: null };
  }
  if (scope === "last_month") {
    // First of last month → first of this month (exclusive end for a clean $lt).
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start, end };
  }
  if (scope === "ytd") {
    return { start: new Date(now.getFullYear(), 0, 1), end: null };
  }
  return { start: null, end: null };
}

// Back-compat shim — some pages still import scopeStartDate. Kept for now so
// this refactor doesn't require touching every caller in one PR.
export function scopeStartDate(scope) {
  return scopeRange(scope).start;
}

/** Returns a filter fn: true → keep the order, false → drop. */
export function makeScopeFilter(scope) {
  const { start, end } = scopeRange(scope);
  if (!start && !end) return () => true;
  const startMs = start ? start.getTime() : null;
  const endMs = end ? end.getTime() : null;
  return (o) => {
    if (!o?.created_at) return true; // never drop rows we can't classify
    const t = new Date(o.created_at).getTime();
    if (!Number.isFinite(t)) return true;
    if (startMs !== null && t < startMs) return false;
    if (endMs !== null && t >= endMs) return false;
    return true;
  };
}
